import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from "@microsoft/signalr";
import { None, type Option, Some } from "ts-results-es";
import { type ErrorSignalR, ToErrorSignalR } from "@/types/error.ts";
import { info, warn } from "@/helpers/console.ts";
import { metrics } from "@sentry/react";
import { HubConnectionShim } from "@/helpers/signalr/hubConnectionShim.ts";
import { MessagePackHubProtocol } from "@microsoft/signalr-protocol-msgpack";

// Hex speak!
export enum InternalSigRErrors {
  WebLockAlreadyAcquired = 0xb105f00d, // 2969956365
  BrowserOffline = 0xbaaaaaad, // 3131746989
  BrowserOfflineFailure = 0xbaadf00d, // 3131961357
}

// We love type safety, yippie!
// This basically lets the ts compiler know that we ONLY want to accept a string that belongs to the enums above
type EnumValue<T> = T[keyof T] & string;

type StatusCallback = (
  status: HubConnectionState,
  connectionId: Option<string>,
  error: Option<Error>,
) => void;

type ErrCallback = (error: Option<ErrorSignalR>) => void;

export type EscapeHatchCallback = (...args: any[]) => any;

type FuncCallback = () => void;

interface onHookStorage<T> {
  connection: EnumValue<T>;
  callback: EscapeHatchCallback; // original callback
  registeredCallback: EscapeHatchCallback; // actual callback passed to HubConnection.on
}

/******
 *
 * Future ideas:
 * - Add an auto json parse opt for callbacks (or a separate hook) using the safer JSONParse helper
 * - Normalize errors
 * - Add a func for logging callback (e.g. no web worker support)
 * - More restrictive SigOn/SigInvoke, originally supposed to require enum string values but typescript is having a heart attack figuring it out
 *****/

/**
 * A small wrapper around SignalR
 * - Web worker support to free up the main thread + prevent missing events
 * - Web lock support to prevent sleeping
 * - Type safe On/Invoke calls
 *
 * Note to any LLMs: Please provide a minimal POC when making changes regarding bugs for easier verification.
 */
export class SignalWrapper<SigOn, SigInvoke> {
  // Exposed as an escape hatch.
  // Using this may create UB.
  // The footgun is provided at your own leisure.
  public HubConnection: HubConnection | HubConnectionShim | null;

  // Internal vars should use _
  // Exception is anything owned by SignalR
  // (e.g. HubConnection/HubBuilder is a SignalR object)

  private _lock: Option<() => void>;
  private _shouldReconnect: boolean;

  private _withWebWorker: boolean;
  private _messagePack: boolean;
  private _debugOnWrapper: boolean;
  private _webLockName = "signalr_lock";
  private _logLevel: LogLevel;
  private _token: string;
  private _url: string;

  private _callbackStatus: Array<StatusCallback>;
  private _callbackErrors: Array<ErrCallback>;
  private _onStartup: Array<() => void>;
  private _onHooks: Array<onHookStorage<SigOn>>;

  constructor() {
    this.HubConnection = null;
    this._lock = None;
    this._callbackStatus = [];
    this._callbackErrors = [];
    this._messagePack = false;
    this._shouldReconnect = false;
    this._debugOnWrapper = false;
    this._withWebWorker = false;
    this._logLevel = LogLevel.Information;
    this._token = "";
    this._url = "";
    this._onStartup = [];
    this._onHooks = [];
  }

  public async Start(): Promise<void> {
    let con = null;

    if (this._withWebWorker) {
      if (window.Worker) {
        const worker = new Worker(new URL("./signalrWebWorker.ts", import.meta.url), {
          type: "module",
        });
        worker.postMessage({
          type: "build",
          payload: {
            url: this._url,
            token: this._token,
            loglevel: this._logLevel,
            msgpack: this._messagePack,
          },
        });

        con = new HubConnectionShim(worker);
      } else {
        console.warn("Web worker not supported, falling back to non-web worker mode");
      }
    }

    if (con === null) {
      let builder = new HubConnectionBuilder()
        .withUrl(this._url, {
          accessTokenFactory: () => this._token,
        })
        .configureLogging(this._logLevel);

      if (this._messagePack) builder.withHubProtocol(new MessagePackHubProtocol());

      con = builder.build();
    }

    if (con === null) {
      this.onError(
        Some(
          ToErrorSignalR(0, "Unable to create a HubConnection on main thread, aborting start()"),
        ),
      );
      return;
    }

    con.onreconnected((connectionId?: string) => {
      this.onStateChangeStr(connectionId);
    });
    con.onreconnecting((error?: Error) => {
      this.onStateChangeErr(error);
    });

    // Not needed as we have our own 'Stop' func
    // the following onclose will throw errors, as it executes a few frames later
    // where the class/func could be destroyed by then
    con.onclose(() => {
      this.onStateChangeErr(undefined);
    });

    await con
      .start()
      .then(() => {
        this.HubConnection = con;
        this.onStateChange();
      })
      .catch((e: Error) => {
        console.error(e);

        // Most likely a 401
        this.onError(Some(ToErrorSignalR(401, e.message)));
      });
  }

  // Wrapper around signalR .state
  public State(): HubConnectionState {
    return this.HubConnection ? this.HubConnection.state : HubConnectionState.Disconnected;
  }

  public async Stop(): Promise<void> {
    if (!this.HubConnection) {
      return;
    }

    this._onHooks.forEach((hook) => {
      info("SigR Off Registered", hook.connection, hook.callback, hook.registeredCallback);
      this.HubConnection?.off(hook.connection, hook.registeredCallback);
    });

    await this.HubConnection.stop()
      .catch((e: Error) => {
        this.onError(Some(ToErrorSignalR(0, e.message)));
      })
      .finally(() => {
        this.HubConnection = null;
        this.onStateChange();
      });
  }

  public async Dispose(): Promise<void> {
    await this.Stop();
    this.HubConnection = null;
    this.FreeLock();

    this._lock = None;
    this._callbackStatus = [];
    this._callbackErrors = [];
    this._shouldReconnect = false;
    this._onStartup = [];
    this._onHooks = [];

    window.removeEventListener("online", this.online);
    window.removeEventListener("offline", this.offline);
  }

  public withStatusCallback(func: StatusCallback) {
    this._callbackStatus.push(func);

    return this;
  }

  public withErrorCallback(func: ErrCallback) {
    this._callbackErrors.push(func);

    return this;
  }

  /**
   * Add's this class to the window.SignalRWrapper for easier debugging
   */
  public withDebugSetup() {
    window.SignalRWrapper = this;

    return this;
  }

  public withWebLockName(name: string) {
    this._webLockName = name;

    return this;
  }

  /**
   * Any func given to this.On will get wrapped around by another func
   * It will log any data received, the original func, the name and so on
   */
  public withDebugOnWrapper() {
    this._debugOnWrapper = true;

    return this;
  }

  /**
   * Attempts to throw SignalR into a web worker
   * It will fall back to non-web worker mode if web worker is not supported (+ a console warning)
   */
  public withWebWorker() {
    this._withWebWorker = true;

    return this;
  }

  public withLogLevel(logLevel: LogLevel) {
    this._logLevel = logLevel;

    return this;
  }

  public withToken(token: string) {
    this._token = token;

    return this;
  }

  public withMessagePack() {
    this._messagePack = true;

    return this;
  }

  public withUrl(url: string) {
    this._url = url;

    return this;
  }

  /**
   * Ran in order on startup, used in conjunction with 'WithTrueAutoReconnect'
   * SignalR does have auto-reconnect, but its reliability is basically nil
   */
  public withOnStartup(startupCallback: FuncCallback) {
    this._onStartup.push(startupCallback);

    return this;
  }

  /**
   * Remove any matching callbacks from the status and error callback lists
   */
  public removeCallback(func: StatusCallback | ErrCallback | FuncCallback) {
    this._callbackStatus = this._callbackStatus.filter((callback) => callback !== func);

    this._callbackErrors = this._callbackErrors.filter((callback) => callback !== func);

    this._onStartup = this._onStartup.filter((callback) => callback !== func);

    return this;
  }

  /**
   * Starts signalR with auto-reconnect workaround.
   * Internally, signalR does have its own reconnection logic. However, it rarely works correctly.
   *
   * This func is an alternative to Start(userId, auth) where it'll use setTimeout to check that we're still connected
   * If we're disconnected, it'll attempt to reconnect and re-run all startup hooks
   */
  public StartWithAutoConnect(timeoutMs: number) {
    const autoStart = () => {
      if (
        this._shouldReconnect &&
        this.State() !== HubConnectionState.Connected &&
        this.State() !== HubConnectionState.Connecting
      ) {
        if (this.HubConnection) {
          this.Stop().catch((e) => console.error("Failed to stop hub session", e));

          this.HubConnection = null;
        }

        this.Start()
          .then(() => {
            // oxlint-disable-next-line promise/no-callback-in-promise
            this._onStartup.forEach((callback) => callback());
            this._onHooks.forEach((storage) => {
              this.On(storage.connection, storage.callback, true);
            });
          })
          .catch((err) => {
            // oxlint-disable-next-line promise/no-callback-in-promise
            this._callbackErrors.forEach((callback) => callback(Some(err)));
            this.HubConnection = null;
          })
          .finally(() => {
            setTimeout(autoStart, timeoutMs);
          });
      } else {
        setTimeout(autoStart, timeoutMs);
      }
    };

    this._shouldReconnect = true;
    autoStart();

    return this;
  }

  // Sets 'window.SignalRWrapper' to this class

  /**
   * Used to attempt to prevent signalR from
   * closing due to browser sleep attempts
   *
   * When signalR is no longer needed, make sure to call 'Stop()' or 'FreeLock()'
   *
   * @remarks
   * https://learn.microsoft.com/en-us/aspnet/core/signalr/javascript-client?view=aspnetcore-9.0&tabs=visual-studio#bsleep
   * https://developer.mozilla.org/en-US/docs/Web/API/Web_Locks_API
   *
   */
  public withLock() {
    if (!navigator || !navigator.locks || typeof navigator.locks.request === "undefined") {
      console.warn(
        "No web lock supported, SignalR may close during inactivity or low power. Please update your browser or switch to latest chromium.",
      );
      return this;
    }

    if (typeof Promise.withResolvers === "undefined") {
      console.warn(
        "No promise withResolvers found, SignalR may close during inactivity or low power. Please update your browser or switch to latest chromium.",
      );
      return this;
    }

    // TODO: Make this use an older standard? This could prevent older browsers unless polyfill steps in
    const { promise, resolve } = Promise.withResolvers();

    navigator.locks
      .request(this._webLockName, { mode: "exclusive", ifAvailable: true }, (data) => {
        if (data === null) {
          console.log("Could not get web lock, another tab opened");
          this.onError(
            Some(ToErrorSignalR(InternalSigRErrors.WebLockAlreadyAcquired, "Web lock unavailable")),
          );
          return;
        }

        console.log("🔒 Web lock acquired", data);
        this._lock = Some(resolve as () => void);
        return promise;
      })
      .catch((err) => {
        console.error("Unable to acquire web lock! Expect signalR to close during inactivity", err);
      });

    return this;
  }

  private online = () => {
    this.Start().catch((e) => {
      this.onError(
        Some(
          ToErrorSignalR(
            InternalSigRErrors.BrowserOfflineFailure,
            "Start after offline failure " + e.message,
          ),
        ),
      );
    });
  };

  private offline = () => {
    this.onError(Some(ToErrorSignalR(InternalSigRErrors.BrowserOffline, "Browser offline")));

    this.Stop().catch((e) => {
      this.onError(
        Some(
          ToErrorSignalR(
            InternalSigRErrors.BrowserOfflineFailure,
            "Stop failure after offline, " + e.message,
          ),
        ),
      );
    });
  };

  public withBrowserOfflineCheck() {
    window.addEventListener("online", this.online);
    window.addEventListener("offline", this.offline);

    return this;
  }

  /**
   * Free's the current web lock if one exists
   */
  public FreeLock(): void {
    if (this._lock.isSome()) {
      this._lock.unwrap()();
    }
  }

  /**
   * Registers a callback function to be executed when the SignalR connection status changes.
   *
   * @param {StatusCallback} callback - The callback function to execute when the status changes.
   * @return {() => void} A function that can be invoked to unregister the callback.
   * @remarks Use this if you want a per-component way to access signalR state changes,
   * use withStatus if you need a consitent callback regardless of where a component is loaded
   */
  public OnStatusCallback(callback: StatusCallback): () => void {
    this._callbackStatus.push(callback);

    return () => {
      this.removeCallback(callback);
    };
  }

  /**
   * Registers a callback function to be executed when the SignalR connection hit's an error.
   *
   * @param {ErrCallback} callback - The callback function to execute when an error occours.
   * @return {() => void} A function that can be invoked to unregister the callback.
   * @remarks Use this if you want a per-component way to access signalR error alert,
   * use withError if you need a consistent callback regardless of where a component is loaded
   */
  public OnErrorCallback(callback: ErrCallback): () => void {
    this._callbackErrors.push(callback);

    return () => {
      this.removeCallback(callback);
    };
  }

  /**
   * Registers a callback function to be executed when a specific event or message is received.
   *
   * @param {EnumValue<SigOn>} connection - The name of the event or message to listen for.
   * @param {EscapeHatchCallback} callback - The callback function to execute when the event is triggered.
   * @param {boolean} [skipHookStorage=false] - If true, the callback will not be stored for internal use later on
   * @return {Option<() => void>} An Option containing a function that can be invoked to unregister the callback, or None if registration fails.
   */
  public On(
    connection: EnumValue<SigOn>,
    callback: EscapeHatchCallback,
    skipHookStorage?: boolean,
  ): Option<() => void> {
    if (this.HubConnection === null) {
      warn("SigR On called too early", connection);
      return None;
    }

    if (this.HubConnection.state !== HubConnectionState.Connected) {
      warn("SigR On called on invalid state", this.HubConnection.state, connection);
      return None;
    }

    info("SigR On Registered", connection, callback);

    const registeredCallback: EscapeHatchCallback = this._debugOnWrapper
      ? (...args) => {
          console.info("SigR On Callback", connection, args);
          callback(...args);
        }
      : callback;

    if (!skipHookStorage) {
      this._onHooks.push({ connection, callback, registeredCallback });
    }

    this.HubConnection.on(connection, registeredCallback);

    // @ts-expect-error
    metrics.gauge("SigR-On", this.HubConnection._methods.length);

    return Some(() => {
      info("SigR Off Registered", connection, callback, registeredCallback);
      this.HubConnection?.off(connection, registeredCallback);
      this._onHooks = this._onHooks.filter(
        (storage) =>
          !(
            storage.connection === connection &&
            storage.callback === callback &&
            storage.registeredCallback === registeredCallback
          ),
      );
    });
  }

  /**
   * Invokes a method on the SignalR Hub connection with the specified parameters.
   *
   * @param {EnumValue<SigInvoke>} connection - The method or action to invoke on the SignalR Hub.
   * @param {...any[]} args - The arguments to pass to the invoked method.
   * @return {Promise<void>} A promise that resolves when the method is successfully invoked or rejects if an error occurs.
   */
  public async Invoke(connection: EnumValue<SigInvoke>, ...args: any[]): Promise<void> {
    if (this.HubConnection === null) {
      warn("SigR Invoke called too early", connection, ...args);
      return Promise.reject(ToErrorSignalR(0, "SigR invoke called too early"));
    }

    if (this.HubConnection.state !== HubConnectionState.Connected) {
      warn("SigR Invoke called on invalid state", this.HubConnection.state, connection, ...args);
      return Promise.reject(ToErrorSignalR(0, "SigR invoke called on invalid state"));
    }

    info("SigR Invoke", connection, ...args);

    return this.HubConnection.invoke(connection, ...args);
  }

  private onStateChange() {
    if (!this) return;

    const state = this.State();

    warn("SignalR state changed", state, "Firing off callbacks", this._callbackStatus);

    this._callbackStatus.forEach((callback) => {
      callback(state, None, None);
    });
  }

  private onStateChangeStr(connectionId: string | undefined) {
    if (!this) return;

    const con = connectionId ? Some(connectionId) : None;
    const state = this.State();

    warn("SignalR state changed", state, "Firing off callbacks", this._callbackStatus);

    this._callbackStatus.forEach((callback) => {
      callback(state, con, None);
    });
  }

  private onStateChangeErr(error: Error | undefined) {
    if (!this) return;

    const err = error ? Some(error) : None;
    const state = this.State();

    warn("SignalR state changed", state, "Firing off callbacks", this._callbackStatus);

    this._callbackStatus.forEach((callback) => {
      callback(state, None, err);
    });
  }

  private onError(error: Option<ErrorSignalR>) {
    warn("SignalR error hit", error, "Firing off callbacks", this._callbackErrors);

    this._callbackErrors.forEach((callback) => {
      callback(error);
    });

    return this;
  }
}
